import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { urlEliminazione, messaggioEliminazione, eConflitto, avvisaConflitto } from '../../utils/eliminazione';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import FormClienteStudio from '../../components/competenze/FormClienteStudio';
import useAggiornamentoPeriodico from '../../hooks/useAggiornamentoPeriodico';
import useVersioniViste from '../../hooks/useVersioniViste';
import ModificataDaAltri from '../../components/ModificataDaAltri';
import { usePuoScrivere } from '../../utils/permessi';
import { TIPI_SOGGETTO } from '../anagrafiche/comuni';   // un solo elenco dei tipi di soggetto


// Anagrafica dei clienti dello studio (Competenze), con i codici azienda parcellazione per fatturante
export default function ClientiStudio() {
  const puoScrivere = usePuoScrivere('competenze');
  const [clienti, setClienti] = useState([]);
  const [fatturanti, setFatturanti] = useState([]);
  const [cerca, setCerca] = useState('');
  const [nonAttivi, setNonAttivi] = useState(false);
  const [inModifica, setInModifica] = useState(null);
  const [errore, setErrore] = useState('');

  const versioni = useVersioniViste();   // l'eliminazione manda la versione che l'operatore ha visto davvero, non quella dell'ultimo aggiornamento automatico
  const carica = useCallback(({ silenzioso = false } = {}) => {
    api.get('/competenze/clienti', { params: { tutti: nonAttivi ? 1 : undefined } }).then(r => { versioni.aggiorna(r.data, { silenzioso }); setClienti(r.data); })
      .catch(err => setErrore(err.response?.data?.errore || 'Errore nel caricamento'));
  }, [nonAttivi, versioni]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { api.get('/competenze/fatturanti').then(r => setFatturanti(r.data)).catch(() => {}); }, []);
  useAggiornamentoPeriodico(() => carica({ silenzioso: true }), { attivo: !inModifica });

  const elimina = async (c) => {
    if (!window.confirm(`Spostare nel Cestino l'azienda "${c.ragione_sociale}"?`)) return;
    try { await api.delete(urlEliminazione(`/competenze/clienti/${c.id}`, versioni.versioneVista(c))); carica(); } catch (err) { setErrore(avvisaConflitto(err, messaggioEliminazione(err))); if (eConflitto(err)) carica(); }
  };

  const attivi = fatturanti.filter(f => f.attivo);
  const nFatt = attivi.length;
  const q = cerca.trim().toLowerCase();
  const filtrati = clienti.filter(c => !q || c.ragione_sociale.toLowerCase().includes(q)
    || (c.codice_fiscale || '').toLowerCase().includes(q) || (c.codice_azienda || '').toLowerCase().includes(q) || Object.values(c.codici_gis || {}).some(x => String(x).includes(q)));

  return (
    <Layout piena>
      <div style={stili.topBar}>
        <h2 style={{ margin: 0 }}>Aziende ({filtrati.length})</h2>
        {puoScrivere && <button onClick={() => setInModifica('nuovo')} style={stili.btn}>+ Nuova azienda</button>}
      </div>
      {errore && <div style={stili.errore}>{errore}</div>}
      <div style={{ display: 'flex', gap: '0.8rem', marginBottom: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="search" placeholder="Cerca per nome, codice fiscale o codice azienda parcellazione…" value={cerca} onChange={e => setCerca(e.target.value)} style={{ ...stili.input, flex: 1 }} />
        <label style={{ fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={nonAttivi} onChange={e => setNonAttivi(e.target.checked)} /> Mostra anche i non seguiti
        </label>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...stili.table, overflow: 'visible' }} className="tabAziende">
          <thead>
            <tr>
              <th rowSpan={nFatt ? 2 : 1} style={stili.th} className="colNome">Ragione sociale</th>
              <th rowSpan={nFatt ? 2 : 1} style={stili.th}>Codice fiscale</th>
              <th rowSpan={nFatt ? 2 : 1} style={{ ...stili.th, whiteSpace: 'normal' }}>Tipo soggetto</th>
              <th rowSpan={nFatt ? 2 : 1} style={{ ...stili.th, whiteSpace: 'normal' }}>Fatturante predefinito</th>
              {nFatt > 0 && <th colSpan={nFatt} style={{ ...stili.th, textAlign: 'center', borderBottom: '1px solid #e5e5e0' }}>Codice azienda parcellazione</th>}
              {puoScrivere && <th rowSpan={nFatt ? 2 : 1} style={stili.th} className="colAzioni">Azioni</th>}
            </tr>
            {nFatt > 0 && (
              <tr>
                {attivi.map(f => <th key={f.id} style={{ ...stili.th, whiteSpace: 'normal', fontSize: '0.74rem', lineHeight: 1.15, maxWidth: '88px', verticalAlign: 'bottom' }} className="intFatt">{f.nome}</th>)}
              </tr>
            )}
          </thead>
          <tbody>
            {filtrati.map(c => (
              <tr key={c.id} style={{ ...stili.tr, opacity: c.attivo ? 1 : 0.5 }}>
                <td style={stili.td} className="colNome">{c.ragione_sociale}{!c.attivo && <small style={{ color: '#999' }}> (non seguito)</small>}{versioni.cambiata(c) && <ModificataDaAltri onRiconosci={() => versioni.riconosci(c)} />}</td>
                <td style={{ ...stili.td, whiteSpace: 'nowrap' }}>{c.codice_fiscale || '—'}</td>
                <td style={stili.td}>{TIPI_SOGGETTO[c.tipo_soggetto] || <span style={{ color: '#999' }}>—</span>}</td>
                <td style={stili.td}>{fatturanti.find(f => f.id === c.fatturante_default_id)?.nome || <span style={{ color: '#999' }}>—</span>}</td>
                {attivi.map(f => <td key={f.id} style={stili.td} className="cellaCodice">{c.codici_gis?.[f.id] || ''}</td>)}
                {puoScrivere && (
                  <td style={{ ...stili.td, whiteSpace: 'nowrap' }} className="colAzioni">
                    <button onClick={() => setInModifica(c)} style={stili.btnSmall}>Modifica</button>
                    <button onClick={() => elimina(c)} style={stili.btnSmallDanger}>Elimina</button>
                  </td>
                )}
              </tr>
            ))}
            {filtrati.length === 0 && <tr><td colSpan={4 + nFatt + (puoScrivere ? 1 : 0)} style={{ ...stili.td, textAlign: 'center', color: '#999' }}>Nessuna azienda</td></tr>}
          </tbody>
        </table>
      </div>
      {inModifica && fatturanti.length > 0 && (
        <FormClienteStudio
          cliente={inModifica === 'nuovo' ? null : inModifica}
          fatturanti={fatturanti}
          onChiudi={() => setInModifica(null)}
          onSalvato={() => { setInModifica(null); carica(); }}
        />
      )}
    </Layout>
  );
}
